import { useState, useEffect } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import AuthModal from '../../../components/auth/AuthModal';

export default function Hero() {
  const { user } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [mathAnimations, setMathAnimations] = useState<string[]>([]);
  const [currentMathIndex, setCurrentMathIndex] = useState(0);
  const [physicsAnimations, setPhysicsAnimations] = useState<string[]>([]);
  const [currentPhysicsIndex, setCurrentPhysicsIndex] = useState(0);
  const [mathLoading, setMathLoading] = useState(true);
  const [physicsLoading, setPhysicsLoading] = useState(true);
  const [mathError, setMathError] = useState<string>('');
  const [physicsError, setPhysicsError] = useState<string>('');

  useEffect(() => {
    // Fetch animation files from Maths Animation folder
    const fetchMathAnimations = async () => {
      try {
        setMathLoading(true);
        setMathError('');
        
        const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
        console.log('🎬 Fetching from Maths Animation folder...');
        console.log('Supabase URL:', supabaseUrl);
        
        const response = await fetch(
          `${supabaseUrl}/functions/v1/get-resources-public?folder=${encodeURIComponent('Maths Animation')}`,
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        console.log('Response status:', response.status);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('❌ Failed to fetch resources:', errorText);
          setMathError('Failed to load animation');
          setMathLoading(false);
          return;
        }

        const result = await response.json();
        console.log('✅ Maths Animation folder response:', result);
        console.log('Number of resources found:', result.resources?.length || 0);
        
        const resources = result.resources || [];
        
        if (resources.length === 0) {
          console.log('⚠️ No files found in "Maths Animation" folder');
          setMathError('No animation files uploaded yet');
          setMathLoading(false);
          return;
        }

        console.log('All resources in folder:', resources.map((r: any) => ({
          title: r.title,
          name: r.name,
          file_type: r.file_type
        })));
        
        // Get all video/gif files
        const mathVideos = resources.filter((r: any) => 
          r.file_type?.startsWith('video/') || 
          r.file_type === 'image/gif' ||
          r.name?.match(/\.(mp4|webm|ogg|gif)$/i)
        );

        if (mathVideos.length > 0) {
          console.log('📁 Found math animation files:', mathVideos.length);
          
          const videoUrls = mathVideos.map((video: any) => {
            if (video.file_url) {
              return video.file_url;
            } else if (video.file_name) {
              return `${supabaseUrl}/storage/v1/object/public/resources/${video.file_name}`;
            } else if (video.file_path) {
              return `${supabaseUrl}/storage/v1/object/public/resources/${video.file_path}`;
            }
            return '';
          }).filter((url: string) => url !== '');
          
          setMathAnimations(videoUrls);
          console.log('🎥 Math animations loaded successfully!');
          console.log('Number of videos:', videoUrls.length);
          setMathError('');
        } else {
          console.log('⚠️ No video/gif files found in Maths Animation folder');
          setMathError('Please upload video files (MP4, WebM, OGG) or GIFs');
        }
        
        setMathLoading(false);
      } catch (error) {
        console.error('❌ Error fetching math animations:', error);
        setMathError('Error loading animations');
        setMathLoading(false);
      }
    };

    fetchMathAnimations();
  }, []);

  useEffect(() => {
    // Fetch animation files from Physics Animation folder
    const fetchPhysicsAnimations = async () => {
      try {
        setPhysicsLoading(true);
        setPhysicsError('');
        
        const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
        console.log('🎬 Fetching from Physics Animation folder...');
        
        const response = await fetch(
          `${supabaseUrl}/functions/v1/get-resources-public?folder=${encodeURIComponent('Physics Animation')}`,
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        console.log('Physics Response status:', response.status);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('❌ Failed to fetch physics resources:', errorText);
          setPhysicsError('Failed to load animation');
          setPhysicsLoading(false);
          return;
        }

        const result = await response.json();
        console.log('✅ Physics Animation folder response:', result);
        console.log('Number of physics resources found:', result.resources?.length || 0);
        
        const resources = result.resources || [];
        
        if (resources.length === 0) {
          console.log('⚠️ No files found in "Physics Animation" folder');
          setPhysicsError('No animation files uploaded yet');
          setPhysicsLoading(false);
          return;
        }

        console.log('All physics resources in folder:', resources.map((r: any) => ({
          title: r.title,
          name: r.name,
          file_type: r.file_type
        })));
        
        // Get all video/gif files
        const physicsVideos = resources.filter((r: any) => 
          r.file_type?.startsWith('video/') || 
          r.file_type === 'image/gif' ||
          r.name?.match(/\.(mp4|webm|ogg|gif)$/i)
        );

        if (physicsVideos.length > 0) {
          console.log('📁 Found physics animation files:', physicsVideos.length);
          
          const videoUrls = physicsVideos.map((video: any) => {
            if (video.file_url) {
              return video.file_url;
            } else if (video.file_name) {
              return `${supabaseUrl}/storage/v1/object/public/resources/${video.file_name}`;
            } else if (video.file_path) {
              return `${supabaseUrl}/storage/v1/object/public/resources/${video.file_path}`;
            }
            return '';
          }).filter((url: string) => url !== '');
          
          setPhysicsAnimations(videoUrls);
          console.log('🎥 Physics animations loaded successfully!');
          console.log('Number of videos:', videoUrls.length);
          setPhysicsError('');
        } else {
          console.log('⚠️ No video/gif files found in Physics Animation folder');
          setPhysicsError('Please upload video files (MP4, WebM, OGG) or GIFs');
        }
        
        setPhysicsLoading(false);
      } catch (error) {
        console.error('❌ Error fetching physics animations:', error);
        setPhysicsError('Error loading animations');
        setPhysicsLoading(false);
      }
    };

    fetchPhysicsAnimations();
  }, []);

  // Handle math video ended - play next video
  const handleMathVideoEnded = () => {
    if (mathAnimations.length > 0) {
      setCurrentMathIndex((prevIndex) => (prevIndex + 1) % mathAnimations.length);
    }
  };

  // Handle physics video ended - play next video
  const handlePhysicsVideoEnded = () => {
    if (physicsAnimations.length > 0) {
      setCurrentPhysicsIndex((prevIndex) => (prevIndex + 1) % physicsAnimations.length);
    }
  };

  return (
    <section className="pt-20 pb-16 bg-gradient-to-br from-blue-50 to-indigo-100 min-h-screen relative overflow-hidden">
      {/* Left Side - Math Animation with Video Background */}
      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[450px] h-96 z-0">
        <div className="relative w-full h-full">
          <div className="w-full h-full rounded-r-3xl shadow-2xl relative overflow-hidden bg-gradient-to-br from-blue-500 to-purple-600">
            {mathLoading ? (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-white text-center">
                  <i className="ri-loader-4-line text-6xl mb-4 animate-spin"></i>
                  <p className="text-sm">Loading animations...</p>
                </div>
              </div>
            ) : mathAnimations.length > 0 ? (
              mathAnimations[currentMathIndex].toLowerCase().endsWith('.gif') ? (
                <img
                  src={mathAnimations[currentMathIndex]}
                  alt="Mathematics Animation"
                  className="w-full h-full object-cover"
                  key={currentMathIndex}
                  onError={(e) => {
                    console.error('❌ Failed to load math GIF:', mathAnimations[currentMathIndex]);
                    setMathError('Failed to load animation file');
                  }}
                  onLoad={() => {
                    console.log('✅ Math GIF loaded successfully');
                    // For GIFs, we need to manually switch after a duration
                    setTimeout(() => {
                      handleMathVideoEnded();
                    }, 5000); // Switch every 5 seconds for GIFs
                  }}
                />
              ) : (
                <video
                  autoPlay
                  muted
                  playsInline
                  className="w-full h-full object-cover"
                  key={currentMathIndex}
                  onEnded={handleMathVideoEnded}
                  onError={(e) => {
                    console.error('❌ Failed to load math video:', mathAnimations[currentMathIndex]);
                    console.error('Video error:', e);
                    setMathError('Failed to load video file');
                  }}
                  onLoadedData={() => console.log('✅ Math video loaded and ready to play')}
                  onPlay={() => console.log('▶️ Math video is playing')}
                >
                  <source src={mathAnimations[currentMathIndex]} type="video/mp4" />
                  <source src={mathAnimations[currentMathIndex]} type="video/webm" />
                  <source src={mathAnimations[currentMathIndex]} type="video/ogg" />
                  Your browser does not support the video tag.
                </video>
              )
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-white text-center px-6">
                  <i className="ri-movie-line text-6xl mb-4 opacity-50"></i>
                  <p className="text-sm opacity-75 mb-2">{mathError || 'No animations found'}</p>
                  <p className="text-xs opacity-60">Upload videos to "Maths Animation" folder</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right Side - Physics Animation with Video Background */}
      <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-96 h-96 z-0">
        <div className="relative w-full h-full">
          <div className="w-full h-full rounded-l-3xl shadow-2xl relative overflow-hidden bg-gradient-to-br from-orange-600 to-purple-600">
            {physicsLoading ? (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-white text-center">
                  <i className="ri-loader-4-line text-6xl mb-4 animate-spin"></i>
                  <p className="text-sm">Loading animations...</p>
                </div>
              </div>
            ) : physicsAnimations.length > 0 ? (
              physicsAnimations[currentPhysicsIndex].toLowerCase().endsWith('.gif') ? (
                <img
                  src={physicsAnimations[currentPhysicsIndex]}
                  alt="Physics Animation"
                  className="w-full h-full object-cover"
                  key={currentPhysicsIndex}
                  onError={(e) => {
                    console.error('❌ Failed to load physics GIF:', physicsAnimations[currentPhysicsIndex]);
                    setPhysicsError('Failed to load animation file');
                  }}
                  onLoad={() => {
                    console.log('✅ Physics GIF loaded successfully');
                    // For GIFs, we need to manually switch after a duration
                    setTimeout(() => {
                      handlePhysicsVideoEnded();
                    }, 5000); // Switch every 5 seconds for GIFs
                  }}
                />
              ) : (
                <video
                  autoPlay
                  muted
                  playsInline
                  className="w-full h-full object-cover"
                  key={currentPhysicsIndex}
                  onEnded={handlePhysicsVideoEnded}
                  onError={(e) => {
                    console.error('❌ Failed to load physics video:', physicsAnimations[currentPhysicsIndex]);
                    console.error('Video error:', e);
                    setPhysicsError('Failed to load video file');
                  }}
                  onLoadedData={() => console.log('✅ Physics video loaded and ready to play')}
                  onPlay={() => console.log('▶️ Physics video is playing')}
                >
                  <source src={physicsAnimations[currentPhysicsIndex]} type="video/mp4" />
                  <source src={physicsAnimations[currentPhysicsIndex]} type="video/webm" />
                  <source src={physicsAnimations[currentPhysicsIndex]} type="video/ogg" />
                  Your browser does not support the video tag.
                </video>
              )
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-white text-center px-6">
                  <i className="ri-movie-line text-6xl mb-4 opacity-50"></i>
                  <p className="text-sm opacity-75 mb-2">{physicsError || 'No animations found'}</p>
                  <p className="text-xs opacity-60">Upload videos to "Physics Animation" folder</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Teacher Profile Section - Left aligned with proper spacing to avoid overlapping */}
        <div className="max-w-3xl mx-auto mb-16 pt-16" style={{ marginLeft: '400px', marginRight: '400px' }}>
          <div className="flex flex-col items-center mb-8">
            <div className="relative mb-6 flex items-center justify-center">
              {/* Left Icon */}
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center shadow-lg mr-8 animate-pulse">
                <i className="ri-book-open-line text-white text-xl"></i>
              </div>
              
              {/* Profile Picture */}
              <img
                src="https://static.readdy.ai/image/6f4307f1f4c2fcea149d2ff1a104f958/771e1e5cab3ed15279386c3bdff8bd24.jpeg"
                alt="Mr. Hidayat Khan"
                className="w-48 h-48 rounded-full object-cover object-top shadow-2xl border-8 border-white"
              />
              
              {/* Right Icon */}
              <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center shadow-lg ml-8 animate-pulse">
                <i className="ri-graduation-cap-line text-white text-xl"></i>
              </div>
            </div>
            
            <h1 className="text-5xl font-bold text-gray-800 mb-4">Mr. Hidayat Khan</h1>
            <p className="text-2xl text-gray-600 mb-6">Maths and Physics Teacher</p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-8 mb-8">
              <div className="flex items-center bg-blue-100 px-4 py-2 rounded-full">
                <span className="text-blue-800 font-semibold">26 Years Experience</span>
              </div>
              <div className="flex items-center bg-purple-100 px-4 py-2 rounded-full">
                <span className="text-purple-800 font-semibold">Mumbai, India</span>
              </div>
            </div>
          </div>

          {/* Main Title */}
          <div className="mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6 leading-tight">
              Welcome to Math&PhysicsVault
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
              Your comprehensive platform for mathematics and physics learning resources. Access quality educational materials, practice problems, and expert guidance to excel in your studies.
            </p>
          </div>

          {/* Action Buttons */}
          {user ? (
            <div className="flex flex-col sm:flex-row gap-4 justify-start items-start">
              <button 
                onClick={() => window.REACT_APP_NAVIGATE('/resources')}
                className="bg-blue-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-blue-700 transition-all duration-300 transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
              >
                <i className="ri-folder-open-line mr-2"></i>
                Explore Resources
              </button>
              <button 
                onClick={() => window.REACT_APP_NAVIGATE('/about')}
                className="border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-full font-semibold text-lg hover:bg-blue-600 hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-user-line mr-2"></i>
                About Me
              </button>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-4 justify-start items-start">
              <button 
                onClick={() => setShowAuthModal(true)}
                className="bg-blue-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-blue-700 transition-all duration-300 transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
              >
                <i className="ri-login-circle-line mr-2"></i>
                Sign In
              </button>
              <button 
                onClick={() => setShowAuthModal(true)}
                className="border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-full font-semibold text-lg hover:bg-blue-600 hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-user-add-line mr-2"></i>
                Register
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Auth Modal */}
      {showAuthModal && (
        <AuthModal onClose={() => setShowAuthModal(false)} />
      )}
    </section>
  );
}
